import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";

// APIs, Context & Hooks
import {
  BOT_ACTIVATE_STEPS,
  BOT_STRATEGIES,
  useBot,
} from "../../context/botContext";

// Components
import DrawerActivateBot1 from "./DrawerActivateBot1";
import DrawerActivateBot2 from "./DrawerActivateBot2";
import DrawerActivateBot3 from "./DrawerActivateBot3";

// --- CONSTANTS ---

export const initSettingRebalance = {
  capital: 0,
  pattern: 0,
  divergence_rate: 0,
  time_designation: 0,
  take_profit_status: false,
  take_profit_value: 0,
  take_profit_percentage: 0,
  follow_market_cap: false,
  allocations: [],
};
export const initSettingGrid = {
  amount: "",
  grid_width: "",
  grid_width_exit: "",
  number_of_grids: "",
  trade_size: "",
  buy_after_bounce: "",
  sell_after_bounce: "",
};
export const initSettingXenoV2 = {
  amount: "",
  grid_width: "",
  buy_flg_return: "",
  grid_width_exit: "",
  exit_flg_return: "",
  number_of_grids: "",
  capital: "",
};
export const initSettingXenoV3 = {
  amount: 0,
  grid_width: 0,
  buy_flg_return: 0,
  grid_width_exit: 0,
  exit_flg_return: 0,
  grid_exit_ratio: 0,
  max_drawdown: 0,
  dd_base_point_all_exit: 0,
  exit_when_touch_dd: 0,
  exit_when_non_touch_dd: 0,
  all_exit_flg_return: 0,
};
export const initSettingDCA = {
  capital: "",
  amount: "",
  max_order_count: "",
  price_change_percentage: "",
  take_profit_percentage: "",
  trigger_price: 0,
  period: 0,
};

// --- MAIN COMPONENT ---

const DrawerActivateBot = ({
  openDialog,
  setOpenDialog,
  setSelectedBot,
  botDetail,
  bots,
  selectedBot,
}) => {
  const location = useLocation();

  const [formRebalance, setFormRebalance] = useState({
    settings: initSettingRebalance,
  });
  const [formDCA, setFormDCA] = useState({ settings: initSettingDCA });
  const [formXenoGrid, setFormXenoGrid] = useState({
    settings: initSettingGrid,
  });
  const [formXenoV2, setFormXenoV2] = useState({ settings: initSettingXenoV2 });
  const [formXenoV3, setFormXenoV3] = useState({ settings: initSettingXenoV3 });

  //  When in edit mode, populate the form directly from botDetail
  useEffect(() => {
    if (openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT && botDetail) {
      const strategy = selectedBot?.bot_name?.toLowerCase();

      // Convert all settings to numbers
      const convertedSettings = {};
      for (const key in botDetail.setting) {
        const value = botDetail.setting[key];
        if (typeof value === "boolean" || value === "") {
          convertedSettings[key] = value;
        } else {
          convertedSettings[key] = Number(value) || 0;
        }
      }

      const editFormData = {
        coin_id: selectedBot.coin_id || 0,
        bot_master_id: selectedBot.id || 0,
        exchange_name: botDetail.exchange_name || "",
        coin_name: botDetail.coin_name || "",
        settings: convertedSettings,
      };

      // Set the appropriate form based on strategy
      switch (strategy) {
        case BOT_STRATEGIES.GRID:
          setFormXenoGrid(editFormData);
          break;
        case BOT_STRATEGIES.XENO_ARC:
          setFormXenoV2(editFormData);
          break;
        case BOT_STRATEGIES.XENO_V3:
          setFormXenoV3(editFormData);
          break;
        case BOT_STRATEGIES.DCA:
          setFormDCA(editFormData);
          break;
        default:
          setFormRebalance(editFormData);
          break;
      }
    }
  }, [openDialog, botDetail, selectedBot]);

  //  Reset forms when opening step 1 or closing
  useEffect(() => {
    if (openDialog === BOT_ACTIVATE_STEPS.STEP_1 || !openDialog) {
      setFormXenoGrid({ settings: initSettingGrid });
      setFormXenoV2({ settings: initSettingXenoV2 });
      setFormXenoV3({ settings: initSettingXenoV3 });
      setFormRebalance({ settings: initSettingRebalance });
      setFormDCA({ settings: initSettingDCA });
    }
  }, [openDialog]);

  const handleClose = () => {
    setOpenDialog(null);
  };

  const isEditMode =
    openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT ||
    openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT;
  const isBotListPage = location.pathname.includes("bot-list");

  const handleBackFromStep2 = () => {
    setOpenDialog(isBotListPage ? null : BOT_ACTIVATE_STEPS.STEP_1);
  };
  const handleNextFromStep2 = () =>
    setOpenDialog(
      isEditMode ? BOT_ACTIVATE_STEPS.STEP_3_EDIT : BOT_ACTIVATE_STEPS.STEP_3
    );
  const handleBackFromStep3 = () =>
    setOpenDialog(
      isEditMode ? BOT_ACTIVATE_STEPS.STEP_2_EDIT : BOT_ACTIVATE_STEPS.STEP_2
    );

  const activeForm = useMemo(() => {
    const strategy = selectedBot?.bot_name?.toLowerCase();
    switch (strategy) {
      case BOT_STRATEGIES.GRID:
        return formXenoGrid;
      case BOT_STRATEGIES.XENO_ARC:
        return formXenoV2;
      case BOT_STRATEGIES.XENO_V3:
        return formXenoV3;
      case BOT_STRATEGIES.DCA:
        return formDCA;
      default:
        return formRebalance;
    }
  }, [
    selectedBot,
    formRebalance,
    formDCA,
    formXenoGrid,
    formXenoV2,
    formXenoV3,
  ]);

  
  return (
    <>
      <DrawerActivateBot1
        open={openDialog === BOT_ACTIVATE_STEPS.STEP_1}
        onClose={handleClose}
        setSelectedBot={setSelectedBot}
        onNext={() => setOpenDialog(BOT_ACTIVATE_STEPS.STEP_2)}
        bots={bots}
      />
      <DrawerActivateBot2
        open={
          openDialog === BOT_ACTIVATE_STEPS.STEP_2 ||
          openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT
        }
        onClose={handleClose}
        onNext={handleNextFromStep2}
        onBack={handleBackFromStep2}
        selectedBot={selectedBot}
        formRebalance={formRebalance}
        setFormRebalance={setFormRebalance}
        formXenoV2={formXenoV2}
        setFormXenoV2={setFormXenoV2}
        formXenoGrid={formXenoGrid}
        setFormXenoGrid={setFormXenoGrid}
        formDCA={formDCA}
        setFormDCA={setFormDCA}
        formXenoV3={formXenoV3}
        setFormXenoV3={setFormXenoV3}
        isEdit={openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT}
        isLoading={false}
      />
      <DrawerActivateBot3
        open={
          openDialog === BOT_ACTIVATE_STEPS.STEP_3 ||
          openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT
        }
        onBack={handleBackFromStep3}
        onClose={handleClose}
        selectedBot={selectedBot}
        onConfirm={handleClose}
        isEdit={openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT}
        form={activeForm}
      />
    </>
  );
};

export default DrawerActivateBot;
